import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companyIntegrationFatorHQueryOptions,
  saveCompanyIntegration,
  CompanyIntegrationFetchError,
} from './company-integration-query';

interface CompanyIntegrationsSectionProps {
  companyId: string;
}

/**
 * "Integrações" section on the company edit screen.
 * Loads the current integration state on mount and lets the operator
 * enable/disable it together with an API key.
 */
export function CompanyIntegrationsSection({ companyId }: CompanyIntegrationsSectionProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery(companyIntegrationFatorHQueryOptions(companyId));

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Seed local state once the query resolves
  useEffect(() => {
    if (data) {
      setEnabled(data.enabled);
      setApiKey('');
      // workspaceId is not a secret — seed with the current value so the operator
      // sees it and can keep/edit it.
      setWorkspaceId(data.workspaceId ?? '');
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: () =>
      saveCompanyIntegration(companyId, {
        enabled,
        ...(enabled && apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        ...(enabled && workspaceId.trim() ? { workspaceId: workspaceId.trim() } : {}),
      }),
    onSuccess: () => {
      setSaveError(null);
      setSaveSuccess(true);
      void queryClient.invalidateQueries({
        queryKey: ['recruiter', 'companies', companyId, 'integrations', 'fator-h'],
      });
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (err) => {
      setSaveSuccess(false);
      if (err instanceof CompanyIntegrationFetchError) {
        if (err.status === 400) {
          // Preferir a mensagem real do backend (ex.: "apiKey deve começar com
          // \"fh_\"") — a genérica só quando o corpo não trouxe o motivo.
          setSaveError(
            err.serverMessage ??
              'Dados inválidos. A chave deve começar com "fh_" e o Workspace ID deve ser um UUID válido.'
          );
          return;
        }
        if (err.status === 404) {
          setSaveError('Empresa não encontrada.');
          return;
        }
      }
      setSaveError('Falha ao salvar integração. Tente novamente.');
    },
  });

  if (isLoading) {
    return (
      <section
        className="flex flex-col gap-4 rounded-lg border border-input bg-card p-6"
        aria-label="Integrações"
      >
        <p className="text-sm text-muted-foreground">Carregando integrações…</p>
      </section>
    );
  }

  if (isError) {
    return (
      <section
        className="flex flex-col gap-4 rounded-lg border border-input bg-card p-6"
        aria-label="Integrações"
      >
        <p className="text-sm text-destructive">Não foi possível carregar as integrações.</p>
      </section>
    );
  }

  return (
    <section
      className="flex flex-col gap-5 rounded-lg border border-input bg-card p-6"
      aria-label="Integrações"
    >
      <h2 className="text-lg font-semibold text-foreground">Integrações</h2>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 rounded-md border border-input bg-background p-4">
          <div className="flex flex-row items-center justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-foreground">FatorH</span>
              {data?.hasKey && data.keyPreview ? (
                <span className="text-xs text-muted-foreground">
                  Chave atual: {data.keyPreview}
                </span>
              ) : null}
            </div>
            <label className="flex cursor-pointer flex-row items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {enabled ? 'Ativado' : 'Desativado'}
              </span>
              <input
                type="checkbox"
                className="size-4 cursor-pointer accent-primary"
                data-testid="company-integration-fatorh-toggle"
                checked={enabled}
                onChange={(e) => {
                  setEnabled(e.target.checked);
                  setSaveError(null);
                  setSaveSuccess(false);
                }}
              />
            </label>
          </div>

          {enabled ? (
            <div className="flex flex-col gap-2">
              <label htmlFor="fatorh-api-key" className="text-sm font-medium text-foreground">
                Chave de API{' '}
                {data?.hasKey ? (
                  <span className="font-normal text-muted-foreground">
                    (deixe em branco para manter a atual)
                  </span>
                ) : null}
              </label>
              <input
                id="fatorh-api-key"
                type="text"
                placeholder="fh_…"
                className="h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="company-integration-fatorh-apikey"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setSaveError(null);
                  setSaveSuccess(false);
                }}
              />

              <label htmlFor="fatorh-workspace-id" className="text-sm font-medium text-foreground">
                Workspace ID{' '}
                <span className="font-normal text-muted-foreground">
                  (UUID do workspace criado no FatorH — obrigatório)
                </span>
              </label>
              <input
                id="fatorh-workspace-id"
                type="text"
                placeholder="00000000-0000-0000-0000-000000000000"
                className="h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="company-integration-fatorh-workspace-id"
                value={workspaceId}
                onChange={(e) => {
                  setWorkspaceId(e.target.value);
                  setSaveError(null);
                  setSaveSuccess(false);
                }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {saveError ? (
        <p className="text-sm text-destructive" role="alert">
          {saveError}
        </p>
      ) : null}

      {saveSuccess ? (
        <p className="text-sm text-green-600" role="status">
          Integração salva com sucesso.
        </p>
      ) : null}

      <div>
        <button
          type="button"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="company-integration-fatorh-save"
          disabled={mutation.isPending}
          onClick={() => {
            if (enabled) {
              if (!apiKey.trim() && !data?.hasKey) {
                setSaveError('Informe a Chave de API (não há chave salva para manter).');
                return;
              }
              if (!workspaceId.trim()) {
                setSaveError('Informe o Workspace ID (obrigatório para ativar a integração).');
                return;
              }
            }
            mutation.mutate();
          }}
        >
          {mutation.isPending ? 'Salvando…' : 'Salvar integração'}
        </button>
      </div>
    </section>
  );
}
